import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken, getAuthUserId } from '../services/apiClient';
import { getMenuByUser, createMenu, updateMenu, MenuDto, SelectedMenuSupplierDto } from '../services/menus';
import { getAllSuppliers, getAllSupplierBranches, SupplierDto, SupplierBranchDto } from '../services/products';
import { getAllCategories, CategoryDto } from '../services/foods';
import { getAllFoodGroups, previewFoodGroup, createFoodGroup, deleteFoodGroup, FoodGroupDto, FoodGroupPreviewFoodDto } from '../services/foodGroups';
import { Allergens } from '../services/ingredients';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import Rating from '@mui/material/Rating';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Allergen labels in Czech
const allergenLabels: { [key: number]: string } = {
  [Allergens.Gluten]: 'Lepek',
  [Allergens.Crustaceans]: 'Korýši',
  [Allergens.Eggs]: 'Vejce',
  [Allergens.Fish]: 'Ryby',
  [Allergens.Peanuts]: 'Arašídy',
  [Allergens.Soybeans]: 'Sója',
  [Allergens.Milk]: 'Mléko',
  [Allergens.Nuts]: 'Ořechy',
  [Allergens.Celery]: 'Celer',
  [Allergens.Mustard]: 'Hořčice',
  [Allergens.Sesame]: 'Sezam',
  [Allergens.Sulphites]: 'Siřičitany',
  [Allergens.Lupin]: 'Vlčí bob',
  [Allergens.Molluscs]: 'Měkkýši',
};

const allergenValues = [
  Allergens.Gluten,
  Allergens.Crustaceans,
  Allergens.Eggs,
  Allergens.Fish,
  Allergens.Peanuts,
  Allergens.Soybeans,
  Allergens.Milk,
  Allergens.Nuts,
  Allergens.Celery,
  Allergens.Mustard,
  Allergens.Sesame,
  Allergens.Sulphites,
  Allergens.Lupin,
  Allergens.Molluscs,
];

interface SupplierSelection {
  supplierId: string;
  selected: boolean;
  branchIds: string[];
}

export default function MenuConfigure() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Menu data
  const [menu, setMenu] = useState<MenuDto | null>(null);
  const [peopleCount, setPeopleCount] = useState(1);
  const [cadenceDays, setCadenceDays] = useState(7);
  const [maxCookTime, setMaxCookTime] = useState<number | ''>('');
  const [minimumRating, setMinimumRating] = useState<number | null>(null);
  const [excludedAllergens, setExcludedAllergens] = useState<Allergens>(Allergens.None);

  // Categories
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [excludedCategoryIds, setExcludedCategoryIds] = useState<string[]>([]);

  // Food groups (predefined menu presets, e.g. "Tradiční česká kuchyně")
  const [foodGroups, setFoodGroups] = useState<FoodGroupDto[]>([]);
  const [selectedFoodGroupIds, setSelectedFoodGroupIds] = useState<string[]>([]);
  const [previewGroup, setPreviewGroup] = useState<FoodGroupDto | null>(null);

  // Create custom group dialog
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupCategoryIds, setNewGroupCategoryIds] = useState<string[]>([]);
  const [newGroupMinDuration, setNewGroupMinDuration] = useState<number | ''>('');
  const [newGroupMaxDuration, setNewGroupMaxDuration] = useState<number | ''>('');
  const [newGroupExcludedAllergens, setNewGroupExcludedAllergens] = useState<Allergens>(Allergens.None);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  // Preview/tick step: filters are only used to fetch a candidate list, the group is created from
  // whichever of those the user leaves ticked (all ticked by default).
  const [previewedFoods, setPreviewedFoods] = useState<FoodGroupPreviewFoodDto[]>([]);
  const [selectedPreviewFoodIds, setSelectedPreviewFoodIds] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  // Suppliers and branches
  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [branches, setBranches] = useState<SupplierBranchDto[]>([]);
  const [supplierSelections, setSupplierSelections] = useState<SupplierSelection[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      // First check if user has a token (is logged in)
      const token = getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // Get user ID from token
      const userId = getAuthUserId();
      console.log('[MenuConfigure] Extracted userId from token:', userId);
      
      // Debug: show full token payload
      try {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          console.log('[MenuConfigure] Full token payload:', payload);
        }
      } catch (e) {
        console.error('[MenuConfigure] Could not parse token:', e);
      }
      
      if (!userId) {
        console.error('Could not extract user ID from token');
        navigate('/login');
        return;
      }

      try {
        setLoading(true);

        // Fetch suppliers, branches, categories and food groups
        let suppliersData;
        let branchesData;
        let categoriesData;
        let foodGroupsData: FoodGroupDto[] = [];

        try {
          [suppliersData, branchesData, categoriesData, foodGroupsData] = await Promise.all([
            getAllSuppliers(),
            getAllSupplierBranches(),
            getAllCategories(),
            getAllFoodGroups(),
          ]);
        } catch (err: unknown) {
          const apiError = err as { status?: number };
          if (apiError.status === 401) {
            console.error('[MenuConfigure] Authentication failed - token may be invalid or expired');
            setError('Přihlášení vypršelo. Prosím přihlaste se znovu.');
            // Clear invalid token and redirect to login
            setTimeout(() => {
              navigate('/login', { state: { from: { pathname: '/menu/configure' } } });
            }, 2000);
            return;
          }
          throw err;
        }

        // Normalize potential $values arrays
        const normalizedSuppliers = Array.isArray(suppliersData)
          ? suppliersData
          : Array.isArray((suppliersData as { $values?: SupplierDto[] })?.$values)
            ? (suppliersData as { $values?: SupplierDto[] }).$values!
            : [];

        const normalizedBranches = Array.isArray(branchesData)
          ? branchesData
          : Array.isArray((branchesData as { $values?: SupplierBranchDto[] })?.$values)
            ? (branchesData as { $values?: SupplierBranchDto[] }).$values!
            : [];

        const normalizedCategories = Array.isArray(categoriesData)
          ? categoriesData
          : Array.isArray((categoriesData as { $values?: CategoryDto[] })?.$values)
            ? (categoriesData as { $values?: CategoryDto[] }).$values!
            : [];

        const normalizedFoodGroups = Array.isArray(foodGroupsData)
          ? foodGroupsData
          : Array.isArray((foodGroupsData as { $values?: FoodGroupDto[] })?.$values)
            ? (foodGroupsData as { $values?: FoodGroupDto[] }).$values!
            : [];

        setSuppliers(normalizedSuppliers);
        setBranches(normalizedBranches);
        setCategories(normalizedCategories);
        setFoodGroups(normalizedFoodGroups.slice().sort((a, b) => a.sortOrder - b.sortOrder));

        // Initialize supplier selections
        const initialSelections: SupplierSelection[] = normalizedSuppliers.map(s => ({
          supplierId: s.id,
          selected: false,
          branchIds: [],
        }));

        // Try to fetch existing menu
        try {
          const userMenu = await getMenuByUser(userId);
          console.log('[MenuConfigure] Loaded menu:', userMenu);
          setMenu(userMenu);

          // Populate form with existing values
          if (userMenu && userMenu.configuration) {
            console.log('[MenuConfigure] Configuration:', userMenu.configuration);
            setPeopleCount(userMenu.configuration.peopleCount || 1);
            setCadenceDays(userMenu.configuration.cadenceDays || 7);
            setMaxCookTime(userMenu.configuration.maximumTimeToCook || '');
            setMinimumRating(userMenu.configuration.minimumRating ?? null);
            setExcludedAllergens(userMenu.configuration.excludedAllergens || Allergens.None);

            // Normalize potential $values array wrapper for excluded categories
            const rawExcludedCategories = userMenu.configuration.excludedCategoryIds;
            const excludedCategoriesList = Array.isArray(rawExcludedCategories)
              ? rawExcludedCategories
              : Array.isArray((rawExcludedCategories as unknown as { $values?: string[] })?.$values)
                ? (rawExcludedCategories as unknown as { $values?: string[] }).$values!
                : [];
            setExcludedCategoryIds(excludedCategoriesList);

            // Normalize potential $values array wrapper for selected food groups
            const rawSelectedFoodGroups = userMenu.configuration.selectedFoodGroupIds;
            const selectedFoodGroupsList = Array.isArray(rawSelectedFoodGroups)
              ? rawSelectedFoodGroups
              : Array.isArray((rawSelectedFoodGroups as unknown as { $values?: string[] })?.$values)
                ? (rawSelectedFoodGroups as unknown as { $values?: string[] }).$values!
                : [];
            setSelectedFoodGroupIds(selectedFoodGroupsList);

            // Set supplier selections from existing config
            // Normalize potential $values array wrapper
            const rawSelectedSuppliers = userMenu.configuration.selectedSuppliers;
            const selectedSuppliersList = Array.isArray(rawSelectedSuppliers)
              ? rawSelectedSuppliers
              : Array.isArray((rawSelectedSuppliers as { $values?: SelectedMenuSupplierDto[] })?.$values)
                ? (rawSelectedSuppliers as { $values?: SelectedMenuSupplierDto[] }).$values!
                : [];

            console.log('[MenuConfigure] Selected suppliers from DB:', selectedSuppliersList);
            console.log('[MenuConfigure] Available suppliers:', normalizedSuppliers.map(s => ({ id: s.id, name: s.name })));

            if (selectedSuppliersList.length > 0) {
              // Create a map for faster lookup - normalize IDs to lowercase for comparison
              const savedSuppliersMap = new Map<string, SelectedMenuSupplierDto>();
              selectedSuppliersList.forEach(s => {
                savedSuppliersMap.set(s.supplierId.toLowerCase(), s);
              });

              console.log('[MenuConfigure] Saved supplier IDs:', Array.from(savedSuppliersMap.keys()));
              console.log('[MenuConfigure] Initial selection supplier IDs:', initialSelections.map(s => s.supplierId.toLowerCase()));

              initialSelections.forEach(sel => {
                const existing = savedSuppliersMap.get(sel.supplierId.toLowerCase());
                if (existing) {
                  console.log('[MenuConfigure] Matched supplier:', sel.supplierId);
                  sel.selected = true;
                  // Handle both branchesIds and potential $values wrapper
                  const rawBranches = existing.branchesIds;
                  sel.branchIds = Array.isArray(rawBranches)
                    ? rawBranches
                    : Array.isArray((rawBranches as unknown as { $values?: string[] })?.$values)
                      ? (rawBranches as unknown as { $values?: string[] }).$values!
                      : [];
                }
              });
            }
          }
        } catch (err: unknown) {
          // Menu doesn't exist yet, that's fine - but log details
          const error = err as { status?: number; message?: string; details?: unknown };
          console.log('[MenuConfigure] No existing menu found:', {
            status: error?.status,
            message: error?.message,
            details: error?.details,
            userId: userId,
          });
        }

        console.log('[MenuConfigure] Final supplier selections:', initialSelections.filter(s => s.selected));
        setSupplierSelections(initialSelections);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load configuration data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleAllergenChange = (allergen: Allergens) => {
    setExcludedAllergens(prev => {
      if ((prev & allergen) === allergen) {
        // Remove allergen
        return prev & ~allergen;
      } else {
        // Add allergen
        return prev | allergen;
      }
    });
  };

  const handleSupplierChange = (supplierId: string, checked: boolean) => {
    setSupplierSelections(prev =>
      prev.map(sel =>
        sel.supplierId === supplierId
          ? { ...sel, selected: checked, branchIds: checked ? sel.branchIds : [] }
          : sel
      )
    );
  };

  const handleFoodGroupToggle = (groupId: string) => {
    setSelectedFoodGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleNewGroupAllergenChange = (allergen: Allergens) => {
    setNewGroupExcludedAllergens(prev => (prev & allergen) === allergen ? prev & ~allergen : prev | allergen);
  };

  const resetCreateGroupForm = () => {
    setNewGroupName('');
    setNewGroupDescription('');
    setNewGroupCategoryIds([]);
    setNewGroupMinDuration('');
    setNewGroupMaxDuration('');
    setNewGroupExcludedAllergens(Allergens.None);
    setPreviewedFoods([]);
    setSelectedPreviewFoodIds([]);
    setPreviewError(null);
    setHasPreviewed(false);
  };

  const handlePreviewFoods = async () => {
    setPreviewError(null);
    try {
      setPreviewLoading(true);
      const response = await previewFoodGroup({
        categoryIds: newGroupCategoryIds.length > 0 ? newGroupCategoryIds : undefined,
        minDurationMinutes: newGroupMinDuration === '' ? undefined : newGroupMinDuration,
        maxDurationMinutes: newGroupMaxDuration === '' ? undefined : newGroupMaxDuration,
        excludedAllergens: newGroupExcludedAllergens,
      });
      const foods = response.foods || [];
      setPreviewedFoods(foods);
      // Everything matched starts ticked - this is what made the old "filters -> save blindly" flow
      // work by default, ticking off individual items is now just an optional refinement on top.
      setSelectedPreviewFoodIds(foods.map(f => f.id));
      setHasPreviewed(true);
      if (foods.length === 0) {
        setPreviewError('Těmto filtrům neodpovídá žádný recept. Upravte filtry a zkuste to znovu.');
      }
    } catch (err) {
      console.error('Error previewing food group:', err);
      setPreviewError('Nepodařilo se načíst jídla odpovídající filtrům. Zkuste to znovu.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTogglePreviewFood = (foodId: string) => {
    setSelectedPreviewFoodIds(prev =>
      prev.includes(foodId) ? prev.filter(id => id !== foodId) : [...prev, foodId]
    );
  };

  const handleToggleSelectAllPreview = () => {
    setSelectedPreviewFoodIds(prev =>
      prev.length === previewedFoods.length ? [] : previewedFoods.map(f => f.id)
    );
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setError('Zadejte název skupiny.');
      return;
    }
    if (selectedPreviewFoodIds.length === 0) {
      setError('Vyberte alespoň jedno jídlo pro skupinu.');
      return;
    }

    try {
      setCreatingGroup(true);
      const created = await createFoodGroup({
        name: newGroupName.trim(),
        description: newGroupDescription.trim(),
        foodIds: selectedPreviewFoodIds,
        categoryIds: newGroupCategoryIds.length > 0 ? newGroupCategoryIds : undefined,
        minDurationMinutes: newGroupMinDuration === '' ? undefined : newGroupMinDuration,
        maxDurationMinutes: newGroupMaxDuration === '' ? undefined : newGroupMaxDuration,
        excludedAllergens: newGroupExcludedAllergens,
      });
      setFoodGroups(prev => [...prev, created]);
      setSelectedFoodGroupIds(prev => [...prev, created.id]);
      setCreateGroupOpen(false);
      resetCreateGroupForm();
      setSuccess('Vlastní skupina byla vytvořena.');
    } catch (err) {
      console.error('Error creating food group:', err);
      setError('Nepodařilo se vytvořit skupinu. Zkuste to znovu.');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      setDeletingGroupId(groupId);
      await deleteFoodGroup(groupId);
      setFoodGroups(prev => prev.filter(g => g.id !== groupId));
      setSelectedFoodGroupIds(prev => prev.filter(id => id !== groupId));
      if (previewGroup?.id === groupId) {
        setPreviewGroup(null);
      }
    } catch (err) {
      console.error('Error deleting food group:', err);
      setError('Nepodařilo se smazat skupinu. Zkuste to znovu.');
    } finally {
      setDeletingGroupId(null);
    }
  };

  const handleBranchChange = (supplierId: string, branchId: string, checked: boolean) => {
    setSupplierSelections(prev =>
      prev.map(sel => {
        if (sel.supplierId !== supplierId) return sel;
        const newBranchIds = checked
          ? [...sel.branchIds, branchId]
          : sel.branchIds.filter(id => id !== branchId);
        return { ...sel, branchIds: newBranchIds };
      })
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const userId = getAuthUserId();
    if (!userId) {
      navigate('/login');
      return;
    }

    // Build selected suppliers array
    const selectedSuppliers: SelectedMenuSupplierDto[] = supplierSelections
      .filter(sel => sel.selected)
      .map(sel => ({
        supplierId: sel.supplierId,
        branchesIds: sel.branchIds.length > 0 ? sel.branchIds : undefined,
      }));

    if (selectedSuppliers.length === 0) {
      setError('Vyberte alespoň jednoho dodavatele.');
      return;
    }

    try {
      setSaving(true);

      if (menu) {
        // Update existing menu
        await updateMenu(menu.id, {
          menuId: menu.id,
          excludedAllergens,
          selectedSuppliers,
          cadenceDays,
          maximumTimeToCook: maxCookTime || undefined,
          peopleCount,
          isCompleted: true,
          minimumRating: minimumRating ?? undefined,
          excludedCategoryIds,
          selectedFoodGroupIds,
        });
      } else {
        // Create new menu
        await createMenu({
          userId,
          excludedAllergens,
          selectedSuppliers,
          cadenceDays,
          maximumTimeToCook: maxCookTime || undefined,
          peopleCount,
          isCompleted: true,
          minimumRating: minimumRating ?? undefined,
          excludedCategoryIds,
          selectedFoodGroupIds,
        });
      }

      setSuccess('Konfigurace byla uložena.');
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      console.error('Error saving menu:', err);
      setError('Nepodařilo se uložit konfiguraci. Zkuste to znovu.');
    } finally {
      setSaving(false);
    }
  };

  const getBranchesForSupplier = (supplierId: string) => {
    return branches.filter(b => b.supplierId === supplierId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleCloseError = () => setError(null);
  const handleCloseSuccess = () => setSuccess(null);

  return (
    <Box sx={{ flexGrow: 1, p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Floating notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" variant="filled" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Typography variant="h4" component="h1" gutterBottom>
        Konfigurace menu
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Nastavte své preference pro generování menu.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Základní nastavení
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <TextField
              label="Počet osob"
              type="number"
              value={peopleCount}
              onChange={(e) => setPeopleCount(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={{ width: 150 }}
            />

            <TextField
              label="Kadence (dní)"
              type="number"
              value={cadenceDays}
              onChange={(e) => setCadenceDays(Math.max(1, parseInt(e.target.value) || 7))}
              inputProps={{ min: 1 }}
              helperText="Kolik dní před opakováním jídla"
              sx={{ width: 200 }}
            />

            <TextField
              label="Max. doba vaření (min)"
              type="number"
              value={maxCookTime}
              onChange={(e) => setMaxCookTime(e.target.value ? parseInt(e.target.value) : '')}
              inputProps={{ min: 0 }}
              helperText="Ponechte prázdné pro bez omezení"
              sx={{ width: 220 }}
            />
          </Box>

          <Box>
            <Typography variant="body1" gutterBottom>
              Minimální hodnocení
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={minimumRating}
                max={5}
                onChange={(_, value) => setMinimumRating(value)}
              />
              {minimumRating != null && (
                <Button size="small" onClick={() => setMinimumRating(null)}>
                  Zrušit filtr
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {minimumRating != null
                ? `Navrhovat pouze jídla s hodnocením alespoň ${minimumRating} z 5 hvězdiček.`
                : 'Bez omezení – navrhovat jídla s libovolným hodnocením.'}
            </Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              Předvolby jídelníčku
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateGroupOpen(true)}>
              Vytvořit vlastní skupinu
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Vyberte styly vaření, ze kterých se budou přednostně navrhovat jídla. Když nevyberete žádný, navrhují se jídla ze všech receptů.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Ostatní filtry níže (alergeny, kategorie, doba vaření, hodnocení) se uplatní i uvnitř vybraných skupin - nic
            se kvůli výběru skupiny nevypíná. Denní návrh cílí na 1 masité a 1 vegetariánské jídlo, ale pokud vybraná
            skupina obsahuje jen jeden typ (např. čistě vegetariánskou skupinu), budou tohoto typu oba návrhy dne.
          </Typography>

          {foodGroups.length === 0 ? (
            <Alert severity="info">Žádné předvolby jídelníčku nejsou zatím k dispozici.</Alert>
          ) : (
            <Grid container spacing={2}>
              {foodGroups.map((group) => {
                const selected = selectedFoodGroupIds.includes(group.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={group.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        position: 'relative',
                        borderColor: selected ? 'primary.main' : undefined,
                        borderWidth: selected ? 2 : 1,
                        backgroundColor: selected ? 'action.selected' : undefined,
                      }}
                    >
                      {group.isCustom && (
                        <IconButton
                          size="small"
                          aria-label="Smazat skupinu"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(group.id);
                          }}
                          disabled={deletingGroupId === group.id}
                          sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
                        >
                          {deletingGroupId === group.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      )}
                      <CardActionArea
                        onClick={() => handleFoodGroupToggle(group.id)}
                        sx={{ height: '100%', p: 1 }}
                      >
                        <CardContent sx={{ pr: group.isCustom ? 4 : undefined }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <RestaurantMenuIcon color={selected ? 'primary' : 'action'} fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="medium">
                              {group.name}
                            </Typography>
                            {group.isCustom && (
                              <Chip label="vlastní" size="small" variant="outlined" sx={{ ml: 'auto', mr: group.isCustom ? 3 : 0 }} />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {group.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={`${group.foodCount} receptů`}
                              color={selected ? 'primary' : 'default'}
                              variant={selected ? 'filled' : 'outlined'}
                            />
                            {group.foods.length > 0 && (
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewGroup(group);
                                }}
                              >
                                Zobrazit recepty
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>

        <Dialog open={previewGroup != null} onClose={() => setPreviewGroup(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {previewGroup?.name}
            <IconButton onClick={() => setPreviewGroup(null)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {previewGroup?.description}
            </Typography>
            <List dense>
              {previewGroup?.foods.map((food) => (
                <ListItem key={food.id} disableGutters>
                  <ListItemText primary={food.name} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                if (previewGroup) handleFoodGroupToggle(previewGroup.id);
                setPreviewGroup(null);
              }}
            >
              {previewGroup && selectedFoodGroupIds.includes(previewGroup.id) ? 'Zrušit výběr' : 'Vybrat tuto skupinu'}
            </Button>
            <Button onClick={() => setPreviewGroup(null)}>Zavřít</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={createGroupOpen}
          onClose={() => { setCreateGroupOpen(false); resetCreateGroupForm(); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Vytvořit vlastní skupinu
            <IconButton onClick={() => { setCreateGroupOpen(false); resetCreateGroupForm(); }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Název"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                fullWidth
                required
                disabled={hasPreviewed}
              />
              <TextField
                label="Popis"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={hasPreviewed}
              />

              {!hasPreviewed ? (
                <>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={categories}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={categories.filter((c) => newGroupCategoryIds.includes(c.id))}
                    onChange={(_, value) => setNewGroupCategoryIds(value.map((c) => c.id))}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                      return (
                        <li key={key} {...optionProps}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Kategorie (nepovinné)" placeholder="Hledat kategorie" />
                    )}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Min. doba vaření (min)"
                      type="number"
                      value={newGroupMinDuration}
                      onChange={(e) => setNewGroupMinDuration(e.target.value ? parseInt(e.target.value) : '')}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                    <TextField
                      label="Max. doba vaření (min)"
                      type="number"
                      value={newGroupMaxDuration}
                      onChange={(e) => setNewGroupMaxDuration(e.target.value ? parseInt(e.target.value) : '')}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Box>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      <Typography variant="body2">Vyloučit alergeny</Typography>
                    </FormLabel>
                    <FormGroup row>
                      {allergenValues.map((allergen) => (
                        <FormControlLabel
                          key={allergen}
                          control={
                            <Checkbox
                              size="small"
                              checked={(newGroupExcludedAllergens & allergen) === allergen}
                              onChange={() => handleNewGroupAllergenChange(allergen)}
                            />
                          }
                          label={allergenLabels[allergen]}
                          sx={{ minWidth: 140 }}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary">
                    Filtry najdou recepty, které pak můžete jednotlivě odškrtnout, než skupinu vytvoříte.
                  </Typography>
                  {previewError && <Alert severity="warning">{previewError}</Alert>}
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      Odpovídá filtrům: <strong>{previewedFoods.length}</strong> receptů, vybráno{' '}
                      <strong>{selectedPreviewFoodIds.length}</strong>
                    </Typography>
                    <Button size="small" onClick={handleToggleSelectAllPreview}>
                      {selectedPreviewFoodIds.length === previewedFoods.length ? 'Zrušit vše' : 'Vybrat vše'}
                    </Button>
                  </Box>
                  <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {previewedFoods.map((food) => (
                        <ListItem key={food.id} disablePadding>
                          <FormControlLabel
                            sx={{ width: '100%', mx: 0, px: 2, py: 0.25 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedPreviewFoodIds.includes(food.id)}
                                onChange={() => handleTogglePreviewFood(food.id)}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <Typography variant="body2">{food.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{food.durationMinutes} min</Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                  <Button size="small" onClick={() => setHasPreviewed(false)} sx={{ alignSelf: 'flex-start' }}>
                    ← Upravit filtry
                  </Button>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {!hasPreviewed ? (
              <Button
                variant="contained"
                onClick={handlePreviewFoods}
                disabled={previewLoading || !newGroupName.trim()}
              >
                {previewLoading ? <CircularProgress size={24} /> : 'Zobrazit jídla'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateGroup}
                disabled={creatingGroup || selectedPreviewFoodIds.length === 0}
              >
                {creatingGroup ? <CircularProgress size={24} /> : `Vytvořit skupinu (${selectedPreviewFoodIds.length})`}
              </Button>
            )}
            <Button onClick={() => { setCreateGroupOpen(false); resetCreateGroupForm(); }} disabled={creatingGroup || previewLoading}>
              Zrušit
            </Button>
          </DialogActions>
        </Dialog>

        <Paper sx={{ p: 3, mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="h6">Vyloučené alergeny</Typography>
            </FormLabel>
            <FormGroup row sx={{ mt: 2 }}>
              {allergenValues.map((allergen) => (
                <FormControlLabel
                  key={allergen}
                  control={
                    <Checkbox
                      checked={(excludedAllergens & allergen) === allergen}
                      onChange={() => handleAllergenChange(allergen)}
                    />
                  }
                  label={allergenLabels[allergen]}
                  sx={{ minWidth: 150 }}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Vyloučené kategorie
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vyberte kategorie jídel, které nechcete v menu navrhovat.
          </Typography>
          {categories.length === 0 ? (
            <Alert severity="info">Žádné kategorie nejsou k dispozici.</Alert>
          ) : (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={categories}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={categories.filter((c) => excludedCategoryIds.includes(c.id))}
              onChange={(_, value) => setExcludedCategoryIds(value.map((c) => c.id))}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Kategorie" placeholder="Hledat kategorie" />
              )}
            />
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Dodavatelé a pobočky
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vyberte dodavatele, u kterých nakupujete. Můžete vybrat konkrétní pobočky nebo nechat prázdné pro všechny pobočky.
          </Typography>

          {suppliers.length === 0 ? (
            <Alert severity="info">Žádní dodavatelé nejsou k dispozici.</Alert>
          ) : (
            suppliers.map((supplier) => {
              const selection = supplierSelections.find(s => s.supplierId === supplier.id);
              const supplierBranches = getBranchesForSupplier(supplier.id);

              return (
                <Accordion key={supplier.id} defaultExpanded={selection?.selected}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      control={
                        <Checkbox
                          checked={selection?.selected || false}
                          onChange={(e) => handleSupplierChange(supplier.id, e.target.checked)}
                        />
                      }
                      label={<Typography fontWeight="medium">{supplier.name}</Typography>}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    {supplierBranches.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Žádné pobočky k dispozici.
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Vyberte konkrétní pobočky nebo nechte prázdné pro všechny:
                        </Typography>
                        <FormGroup>
                          {supplierBranches.map((branch) => (
                            <FormControlLabel
                              key={branch.id}
                              control={
                                <Checkbox
                                  checked={selection?.branchIds.includes(branch.id) || false}
                                  onChange={(e) => handleBranchChange(supplier.id, branch.id, e.target.checked)}
                                  disabled={!selection?.selected}
                                />
                              }
                              label={branch.name}
                            />
                          ))}
                        </FormGroup>
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            disabled={saving}
          >
            Zrušit
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Uložit konfiguraci'}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
